import type { TpePaymentMethod, TpePaymentStatus } from "cadendar-shared";
import { DateTime } from "luxon";
import { formaters } from "vincent-utils";
import {
  isSettledTpePayment,
  statusBadgeClasses,
  statusLabels,
} from "../display.ts";
import {
  TpePaymentAmount,
  TpePaymentCorrectionControls,
} from "./TpePaymentCorrectionControls.tsx";

export interface TpePaymentsTableRow {
  id: string;
  amount_cents: number;
  accounting_amount_cents: number;
  status: TpePaymentStatus;
  initiated_at: Date | string;
  authorization: string | null;
  payment_method: TpePaymentMethod | null;
  payment_method_locked_at: Date | string | null;
  card_read_mode: string | null;
  patient_name?: string;
  patient_forename?: string;
}

export function TpePaymentsTable({
  payments,
  onSetPaymentMethod,
  onUnlockForCorrection,
  onCancelCorrection,
  onSaveCorrection,
  isPending,
}: {
  payments: TpePaymentsTableRow[];
  onSetPaymentMethod: (
    paymentId: string,
    method: TpePaymentMethod,
  ) => Promise<void>;
  onUnlockForCorrection: (paymentId: string) => Promise<void>;
  onCancelCorrection: (paymentId: string) => Promise<void>;
  onSaveCorrection: (options: {
    paymentId: string;
    paymentMethod: TpePaymentMethod;
    accountingAmountCents: number;
  }) => Promise<void>;
  isPending: boolean;
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-slate-500 border-b border-slate-200">
          <th className="py-2 pr-3 font-medium">Heure</th>
          <th className="py-2 pr-3 font-medium">Patient</th>
          <th className="py-2 pr-3 font-medium">Montant</th>
          <th className="py-2 pr-3 font-medium">Statut</th>
          <th className="py-2 pr-3 font-medium">Mode</th>
          <th className="py-2 font-medium">Autorisation</th>
        </tr>
      </thead>
      <tbody>
        {payments.map((payment) => {
          const status = payment.status;
          const patientDisplay =
            payment.patient_forename && payment.patient_name
              ? formaters.formatNameForenameForDisplay({
                  name: payment.patient_name,
                  forename: payment.patient_forename,
                })
              : null;
          return (
            <tr key={payment.id} className="border-b border-slate-100">
              <td className="py-2 pr-3 text-slate-700">
                {DateTime.fromISO(
                  payment.initiated_at instanceof Date
                    ? payment.initiated_at.toISOString()
                    : payment.initiated_at,
                ).toFormat("HH:mm")}
              </td>
              <td className="py-2 pr-3 text-slate-700">
                {patientDisplay
                  ? `${patientDisplay.name} ${patientDisplay.forename}`
                  : "-"}
              </td>
              <td className="py-2 pr-3 text-slate-700">
                <TpePaymentAmount
                  amountCents={payment.amount_cents}
                  accountingAmountCents={payment.accounting_amount_cents}
                  originalAmountLabel={
                    payment.status === "manually_recorded"
                      ? "Saisie initiale"
                      : "TPE"
                  }
                />
              </td>
              <td className="py-2 pr-3">
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClasses[status]}`}
                >
                  {statusLabels[status]}
                </span>
              </td>
              <td className="py-2 pr-3">
                {isSettledTpePayment(status) ? (
                  <TpePaymentCorrectionControls
                    payment={payment}
                    onSetPaymentMethod={onSetPaymentMethod}
                    onUnlockForCorrection={onUnlockForCorrection}
                    onCancelCorrection={onCancelCorrection}
                    onSaveCorrection={onSaveCorrection}
                    disabled={isPending}
                  />
                ) : (
                  <span className="text-slate-400">-</span>
                )}
              </td>
              <td className="py-2 text-slate-500">
                {payment.authorization ?? "-"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
